import { useEffect, useState } from 'react';
import { api, ConfirmDialog, PillButton, apiErrorMessage, formatMoney } from '@aralere/shared-ui';
import type {
  CreditLapseRef,
  CreditLineReason,
  PendingCredit,
  PendingCreditTrigger,
  PendingCreditsResponse,
  ResolvePendingCreditsResponse,
} from '@aralere/shared-types';

// ──────────────────────────────────────────────────────────────────────────────
// 04 #160 WS-C M2b (02i §8 DP-13; 02j item 19): the LAPSE DIALOG. Opened when no positive recurring
// line is left to absorb a credit entitlement the composer would otherwise carry forever: the last
// billed unit's cancel and a package removal leaving none (ClientServicesPanel, from the mutation
// response), a client archive (Clients.tsx, PRE-CHECKED and blocking: an archived client never reaches
// the composer, so nothing would re-raise a dismissed dialog), a compose with carry and no positive
// line (Invoices.tsx, from nothing_to_compose) and the on-demand read (the Invoices page).
//
// Per entitlement the admin chooses:
//   credit note  - an ISSUED source: a credit note against the invoice that billed the days (the
//                  existing line-referencing path; tax from the frozen taxableNet); the server writes it.
//   refund       - a PAID source: the qualified refund + its paired credit note (the path that already
//                  exists for a paid invoice); the SPA opens the refund dialog pre-filled (onRefund),
//                  one source invoice at a time - any other paid-source entitlement is re-raised by the
//                  on-demand read.
//   lapse        - the entitlement is forgone: stored on the unit's own row (metadata.lapsedCredits[],
//                  voidable below), consumed by the composer's ledger, audited (credit.lapsed).
//   keep carrying - nothing is written; the next compose re-derives it. CLOSING the dialog keeps
//                  carrying ALWAYS (owner ruling s249 Q3): the brand default is pre-selected, never
//                  applied on close. On an ARCHIVE keep-carrying is a dead end and says so.
// A WRITTEN-OFF source offers neither note nor refund (the days are already bad debt): lapse is
// pre-selected. Otherwise the brand's proration.unabsorbedCredit pre-selects (absent = credit-note);
// when the credit-note / refund choice is not offered, keep carrying is pre-selected instead.
// A click outside the dialog does NOT close it (owner rule s249, found at the control run: a dialog
// that may not come back on its own if dismissed by accident is answered with a button) - the
// Keep carrying / Cancel archive button is the only way out, and it says what it does.
// Every entitlement names ONLY the days of its run not already settled in full by an earlier dated
// credit (recurring.ts#settledDays / openRuns, the s249 fix 4): the figure is the run's remainder, the
// days the open ones - so a note or lapse raised here never shares a day's entitlement with an earlier
// one. The refund branch carries, per source line, the FIRST run of adjacent entitlements (refundEligible).
//
// The paid-source branch is offered whenever the source is paid: the refund dialog's "return part of
// the invoice" mode ALWAYS pairs the credit note (Invoices.tsx#doRefund, createLinkedCreditNote =
// qualified), so revenue and tax are reversed with the cash. PAID_BRANCH_REQUIRES_AUTO_PAIR flips the
// branch to "only when businesses.autoCreditNoteOnRefund is on" (the s249 Q1 ruling as worded; the
// setting seeds that checkbox on a hand refund but the return mode does not consult it).
// ──────────────────────────────────────────────────────────────────────────────

const PAID_BRANCH_REQUIRES_AUTO_PAIR = false;

/** The day after a YYYY-MM-DD day (UTC arithmetic on the day's own digits; no clock, no zone). */
function nextDay(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** M2b fix 4 (the s249 control-run finding): the entitlements the refund branch can carry at once. The
 *  refund dialog carries ONE amount and ONE span per source line (its paired credit note names a line once,
 *  credit_line_duplicate), so on each source line only the FIRST run of entitlements adjacent by day rides a
 *  refund; a later, separate run on the same line (a settled stretch between them) is not offered the refund
 *  until that one is recorded - the credit stays owed and the next read offers it again. Returns the keys
 *  that may take the refund choice. */
export function refundEligible(credits: readonly PendingCredit[]): Set<string> {
  const byLine = new Map<string, PendingCredit[]>();
  for (const c of credits) {
    const k = `${c.source.invoiceId}|${c.source.lineId}`;
    byLine.set(k, [...(byLine.get(k) ?? []), c]);
  }
  const out = new Set<string>();
  for (const list of byLine.values()) {
    const sorted = [...list].sort((a, b) => (a.coversFrom < b.coversFrom ? -1 : a.coversFrom > b.coversFrom ? 1 : 0));
    let prevTo: string | null = null;
    for (const c of sorted) {
      if (prevTo !== null && c.coversFrom !== nextDay(prevTo)) break;
      out.add(c.key);
      prevTo = c.coversTo;
    }
  }
  return out;
}

/** The refund dialog's pre-fill for ONE paid source invoice's entitlements: one row per source LINE, the list
 *  amounts summed and the days the hull of the entitlements' days - of the refund-eligible ones only
 *  (refundEligible: on a line, the first run of ADJACENT entitlements, so the hull holds no day another entry
 *  settled; the refund dialog carries one amount and one span per line, and the paired note names them,
 *  02i T1.6 - M2b fix 4 replaced the s249 fix-3 hull over every entitlement on the line, which put a
 *  settled day on the note). */
export function refundPrefill(credits: PendingCredit[]): Array<{ lineId: string; amount: number; coversFrom: string; coversTo: string }> {
  const byLine = new Map<string, { lineId: string; amount: number; coversFrom: string; coversTo: string }>();
  const eligible = refundEligible(credits);
  for (const c of credits) {
    if (!eligible.has(c.key)) continue;
    const cur = byLine.get(c.source.lineId);
    if (!cur) byLine.set(c.source.lineId, { lineId: c.source.lineId, amount: c.creditNoteAmount, coversFrom: c.coversFrom, coversTo: c.coversTo });
    else byLine.set(c.source.lineId, { lineId: c.source.lineId, amount: Math.round((cur.amount + c.creditNoteAmount) * 100) / 100, coversFrom: cur.coversFrom < c.coversFrom ? cur.coversFrom : c.coversFrom, coversTo: cur.coversTo > c.coversTo ? cur.coversTo : c.coversTo });
  }
  return [...byLine.values()];
}

/** The consume-once deep link that opens the Invoices page's refund dialog pre-filled for ONE paid source
 *  invoice's entitlements (Invoices.tsx reads `refund=<lineId>:<listAmount>:<from>:<to>[,...]` beside
 *  client + open; the days ride so the paired credit note names them, 02i T1.6). */
export function refundDeepLink(clientId: string, credits: PendingCredit[]): string {
  const first = credits[0];
  const lines = refundPrefill(credits).map((r) => `${encodeURIComponent(r.lineId)}:${r.amount.toFixed(2)}:${r.coversFrom}:${r.coversTo}`).join(',');
  return `/admin/invoices?client=${encodeURIComponent(clientId)}&open=${encodeURIComponent(first.source.invoiceId)}&refund=${lines}`;
}

/** One sentence for the flash after a resolve. */
export function resolvedSummary(res: ResolvePendingCreditsResponse): string {
  const parts: string[] = [];
  if (res.creditNotes.length > 0) parts.push(`${res.creditNotes.length === 1 ? 'Credit note' : 'Credit notes'} issued: ${res.creditNotes.map((n) => `${n.adjustment.documentNumber ?? n.adjustment.id} for ${formatMoney(n.adjustment.amount)}`).join(', ')}`);
  if (res.lapses.length > 0) parts.push(`${res.lapses.length} credit${res.lapses.length === 1 ? '' : 's'} lapsed`);
  if (res.carried.length > 0) parts.push(`${res.carried.length} still carrying`);
  return parts.length > 0 ? `${parts.join('; ')}.` : 'Nothing changed.';
}

export type PendingCreditUiChoice = 'credit-note' | 'refund' | 'lapse' | 'carry';

const REASON_LABEL: Record<CreditLineReason, string> = {
  'catalog-disabled': 'disabled in the catalog',
  'suspended-time': 'suspended for non-payment',
  'package-switch': 'replaced by a package switch or adoption',
};

const STATE_LABEL: Record<string, string> = { issued: 'issued, unpaid', paid: 'paid', 'written-off': 'written off' };

const TRIGGER_INTRO: Record<PendingCreditTrigger, string> = {
  cancel: 'The service just cancelled was the last recurring charge this client had',
  'package-remove': 'The package just removed was the last recurring charge this client had',
  archive: 'Archiving this client leaves no recurring charge',
  compose: 'That month composes no recurring charge',
  manual: 'No recurring charge is left',
};

/** What the dialog pre-selects for one entitlement (02i §8 DP-13; owner ruling s249 Q1). `all` = every
 *  entitlement in the read (refundEligible needs the line's others; absent = this one alone). */
export function preselect(c: PendingCredit, defaults: PendingCreditsResponse['defaults'], all: readonly PendingCredit[] = [c]): PendingCreditUiChoice {
  if (c.source.state === 'written-off') return 'lapse';
  if (defaults.unabsorbedCredit === 'lapse') return 'lapse';
  if (c.source.state === 'issued') return c.creditNoteAmount > 0 ? 'credit-note' : 'carry';
  if (c.source.state === 'paid') return choiceDisabledReason(c, 'refund', defaults, all) === null ? 'refund' : 'carry';
  return 'carry';
}

/** Why a choice is not offered on this entitlement (null = offered). `all` as on preselect. */
export function choiceDisabledReason(c: PendingCredit, choice: PendingCreditUiChoice, defaults: PendingCreditsResponse['defaults'], all: readonly PendingCredit[] = [c]): string | null {
  const doc = c.source.documentNumber ?? c.source.invoiceId;
  if (choice === 'credit-note') {
    if (c.source.state === 'paid') return `${doc} is paid: return the credit as a refund with its paired credit note instead.`;
    if (c.source.state === 'written-off') return `${doc} is written off: those days are already bad debt, so no credit note can be raised against it.`;
    if (!(c.creditNoteAmount > 0)) return `Nothing is left to credit-note on the line ${doc} billed this on.`;
    return null;
  }
  if (choice === 'refund') {
    if (c.source.state === 'issued') return `${doc} is unpaid: a credit note reduces what is owed instead.`;
    if (c.source.state === 'written-off') return `${doc} is written off: no cash was received for those days.`;
    if (PAID_BRANCH_REQUIRES_AUTO_PAIR && !defaults.autoCreditNoteOnRefund)
      return 'A refund without its paired credit note would leave the revenue and tax on the books. Turn on "credit note on refund" in Settings to offer it here.';
    if (!refundEligible(all).has(c.key))
      return `An earlier run of days on the same line of ${doc} comes first: record that refund, and this one is offered again (a refund carries one run per line). Lapse it or keep carrying meanwhile.`;
    return null;
  }
  return null;
}

export function PendingCreditsDialog({
  pending,
  trigger,
  clientName,
  onClose,
  onResolved,
}: {
  /** Null = closed. The dialog keeps its own copy so a partial failure or a void re-renders from the server's re-read. */
  pending: PendingCreditsResponse | null;
  trigger: PendingCreditTrigger;
  clientName: string;
  /** Close = keep carrying (nothing written). On the archive trigger the caller aborts the archive. */
  onClose: () => void;
  /** Every non-refund decision was written (or nothing needed writing). `refunds` = the paid-source
   *  entitlements the admin chose to refund, grouped per source invoice, for the caller to route. */
  onResolved: (res: ResolvePendingCreditsResponse | null, refunds: PendingCredit[][]) => void;
}): JSX.Element | null {
  const [state, setState] = useState<PendingCreditsResponse | null>(pending);
  const [choices, setChoices] = useState<Record<string, PendingCreditUiChoice>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [voidBusy, setVoidBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [releaseAsk, setReleaseAsk] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [voidTarget, setVoidTarget] = useState<CreditLapseRef | null>(null);
  const [voidReason, setVoidReason] = useState('');

  // (Re)seed from the server state whenever the dialog opens with a new read; keep the admin's choices
  // for the keys that survive a re-read.
  useEffect(() => {
    setState(pending);
    if (!pending) return;
    setChoices((prev) => {
      const next: Record<string, PendingCreditUiChoice> = {};
      for (const c of pending.credits) next[c.key] = prev[c.key] ?? preselect(c, pending.defaults, pending.credits);
      return next;
    });
    setErr(null);
    setReleaseAsk(false);
    setConfirmRelease(false);
  }, [pending]);

  if (!pending || !state) return null;

  const defaults = state.defaults;
  const byUnit = new Map<string, PendingCredit[]>();
  for (const c of state.credits) byUnit.set(c.unit, [...(byUnit.get(c.unit) ?? []), c]);
  const periodLabel = `${state.period.start.slice(0, 7)}`;
  const total = `${formatMoney(state.totals.amount)}${state.totals.taxAmount > 0 ? ` plus ${formatMoney(state.totals.taxAmount)} of tax` : ''}`;
  const anyRefund = state.credits.some((c) => choices[c.key] === 'refund');
  const anyWrite = state.credits.some((c) => choices[c.key] === 'credit-note' || choices[c.key] === 'lapse');
  const allCarry = state.credits.length > 0 && !anyRefund && !anyWrite;

  const applyServerState = (next: PendingCreditsResponse): void => {
    setState(next);
    setChoices((prev) => {
      const out: Record<string, PendingCreditUiChoice> = {};
      for (const c of next.credits) out[c.key] = prev[c.key] ?? preselect(c, next.defaults, next.credits);
      return out;
    });
  };

  const confirm = async (): Promise<void> => {
    setErr(null);
    const refunds: PendingCredit[][] = [];
    const byInvoice = new Map<string, PendingCredit[]>();
    for (const c of state.credits) {
      if (choices[c.key] !== 'refund') continue;
      byInvoice.set(c.source.invoiceId, [...(byInvoice.get(c.source.invoiceId) ?? []), c]);
    }
    for (const group of byInvoice.values()) refunds.push(group);
    const decisions = state.credits
      .filter((c) => choices[c.key] === 'credit-note' || choices[c.key] === 'lapse')
      .map((c) => ({ key: c.key, choice: choices[c.key] as 'credit-note' | 'lapse', ...(choices[c.key] === 'lapse' && notes[c.key]?.trim() ? { note: notes[c.key].trim() } : {}) }));
    if (decisions.length === 0) {
      onResolved(null, refunds);
      return;
    }
    setBusy(true);
    try {
      const res = await api.admin.resolvePendingCredits({
        clientId: state.clientId,
        trigger,
        period: state.period.start.slice(0, 7),
        decisions,
        ...(confirmRelease ? { confirmRelease: true } : {}),
      });
      if (res.failed) {
        // Partial: everything before the failure stands (res.pending is the state after it); nothing after
        // it was attempted. Re-render from the server's re-read and say exactly what happened.
        applyServerState(res.pending);
        const done = res.creditNotes.length + res.lapses.length;
        if (res.failed.code === 'would_release_documents') {
          setReleaseAsk(true);
          setErr(`${res.failed.message} Tick the box below and apply again to confirm the release.${done > 0 ? ` (${done} earlier choice${done === 1 ? '' : 's'} already applied.)` : ''}`);
        } else {
          setErr(`${res.failed.message} (${res.failed.code})${done > 0 ? ` ${done} earlier choice${done === 1 ? ' was' : 's were'} applied;` : ''}${res.notAttempted.length > 0 ? ` ${res.notAttempted.length} later choice${res.notAttempted.length === 1 ? ' was' : 's were'} not attempted.` : ''}`);
        }
        return;
      }
      onResolved(res, refunds);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doVoid = async (): Promise<void> => {
    if (!voidTarget) return;
    if (!voidReason.trim()) {
      setErr('A reason is required to void a lapsed credit.');
      return;
    }
    setVoidBusy(voidTarget.id);
    setErr(null);
    try {
      const res = await api.admin.voidCreditLapse({ clientId: state.clientId, rowKind: voidTarget.rowKind, rowId: voidTarget.rowId, lapseId: voidTarget.id, reason: voidReason.trim() });
      setVoidTarget(null);
      setVoidReason('');
      applyServerState(res.pending);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setVoidBusy(null);
    }
  };

  const cancelLabel = trigger === 'archive' ? 'Cancel archive' : 'Keep carrying';
  const confirmLabel = trigger === 'archive' ? (anyRefund ? 'Record refund, then archive again' : 'Apply and archive') : anyRefund && !anyWrite ? 'Record refund' : 'Apply choices';

  const choiceButton = (c: PendingCredit, choice: PendingCreditUiChoice, label: string): JSX.Element => {
    const why = choiceDisabledReason(c, choice, defaults, state.credits);
    const selected = choices[c.key] === choice;
    return (
      <PillButton
        size="xs"
        tone={selected ? (choice === 'lapse' ? 'amber' : choice === 'carry' ? 'neutral' : 'green') : 'neutral'}
        className={selected ? 'ring-2 ring-[var(--color-accent)] ring-offset-1' : ''}
        aria-pressed={selected}
        disabled={why !== null || busy}
        title={why ?? undefined}
        onClick={() => setChoices((m) => ({ ...m, [c.key]: choice }))}
      >
        {label}
      </PillButton>
    );
  };

  return (
    <>
      <ConfirmDialog
        open
        size="lg"
        dismissOnBackdrop={false}
        title="Credit still owed to the client"
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        busy={busy}
        confirmDisabled={state.credits.length === 0 && trigger !== 'archive'}
        onConfirm={() => void confirm()}
        onCancel={onClose}
      >
        {state.credits.length === 0 ? (
          <p>
            Nothing is owed to {clientName} any more{state.lapses.length > 0 ? '; the lapsed credits below can still be voided' : ''}.
          </p>
        ) : (
          <>
            <p>
              {trigger === 'archive'
                ? `${TRIGGER_INTRO.archive} and an archived client is never composed, so the ${total} of credit owed to ${clientName} would never be absorbed. Choose what happens to each credit before the archive runs.`
                : state.positiveLineCount > 0
                  ? `${total} of credit is owed to ${clientName}; the next composed invoice (${periodLabel}, ${state.positiveLineCount} recurring ${state.positiveLineCount === 1 ? 'line' : 'lines'}) would absorb it as a credit line. You can settle it now instead: a credit note or a refund against the invoice that billed the days, or a lapse. Closing this dialog keeps carrying it.`
                  : `${TRIGGER_INTRO[trigger]}, so the ${total} of credit owed to ${clientName} has nothing to absorb it (${periodLabel} would compose no positive line). Choose what happens to each credit. Closing this dialog keeps carrying it: nothing is written, and the next composed invoice re-derives it.`}
            </p>
            {trigger === 'archive' ? (
              <p className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-[var(--tint-warn-text)]">
                An archived client is never composed, so a credit left carrying is a dead end: it stays owed and nothing re-raises it. Choose a credit note, a refund, or lapse for each, or cancel the archive.
              </p>
            ) : null}
            {state.warnings.length > 0 ? (
              <ul className="list-disc space-y-1 pl-5 text-xs text-[var(--color-muted)]">
                {state.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            ) : null}
            <div className="space-y-3">
              {[...byUnit.entries()].map(([unit, list]) => (
                <div key={unit} className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
                  <div className="font-semibold">{unit}</div>
                  <div className="mt-2 space-y-3">
                    {list.map((c) => {
                      const doc = c.source.documentNumber ?? c.source.invoiceId;
                      const chosen = choices[c.key] ?? 'carry';
                      const why = chosen === 'carry' ? null : choiceDisabledReason(c, chosen, defaults, state.credits);
                      return (
                        <div key={c.key} className="space-y-1.5">
                          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                            <span>
                              {c.coversFrom} to {c.coversTo} ({c.days} of {c.sourceDays} days {REASON_LABEL[c.reason]}), billed on {doc} ({STATE_LABEL[c.source.state] ?? c.source.state})
                            </span>
                            <span className="font-semibold">
                              {formatMoney(c.amount)}
                              {c.taxAmount > 0 ? <span className="font-normal text-[var(--color-muted)]"> + {formatMoney(c.taxAmount)} tax</span> : null}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5" role="group" aria-label={`What happens to the credit for ${unit}, ${c.coversFrom} to ${c.coversTo}`}>
                            {choiceButton(c, 'credit-note', c.source.state === 'issued' && c.creditNoteAmount > 0 ? `Credit note (${formatMoney(c.creditNoteAmount)} list)` : 'Credit note')}
                            {choiceButton(c, 'refund', 'Refund')}
                            {choiceButton(c, 'lapse', 'Lapse')}
                            {choiceButton(c, 'carry', 'Keep carrying')}
                          </div>
                          {why ? <div className="text-xs text-[var(--tint-red-text)]">{why}</div> : null}
                          {chosen === 'credit-note' ? (
                            <div className="text-xs text-[var(--color-muted)]">A credit note against {doc} for {formatMoney(c.creditNoteAmount)} of list net (the tax follows the line's frozen taxed share) reduces what the client owes on it.</div>
                          ) : chosen === 'refund' ? (
                            <div className="text-xs text-[var(--color-muted)]">Opens the refund dialog for {doc} with this line pre-filled: the cash returned is the line's net plus its tax, and the paired credit note reverses both.{trigger === 'archive' ? ' The archive does not run; archive the client again after recording the refund.' : ''}</div>
                          ) : chosen === 'lapse' ? (
                            <div className="space-y-1">
                              <div className="text-xs text-[var(--color-muted)]">The credit is forgone and recorded on the assignment (voidable later); nothing is issued to the client.</div>
                              <input
                                className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-xs"
                                placeholder="Note (optional)"
                                value={notes[c.key] ?? ''}
                                maxLength={500}
                                onChange={(e) => setNotes((m) => ({ ...m, [c.key]: e.target.value }))}
                              />
                            </div>
                          ) : (
                            <div className="text-xs text-[var(--color-muted)]">{trigger === 'archive' ? 'Dead end on an archived client (see above).' : 'Nothing is written; the next composed invoice re-derives it.'}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {allCarry && trigger !== 'archive' ? <p className="text-xs text-[var(--color-muted)]">Every credit is set to keep carrying, so applying changes nothing.</p> : null}
          </>
        )}
        {state.lapses.length > 0 ? (
          <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-3">
            <div className="font-semibold">Lapsed credits on this client</div>
            <div className="mt-1 text-xs text-[var(--color-muted)]">Forgone earlier. Voiding one puts its entitlement back: the next composed invoice re-derives it.</div>
            <ul className="mt-2 space-y-1.5">
              {state.lapses.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span>
                    {l.unit}: {l.coversFrom} to {l.coversTo}, {formatMoney(l.amount)}
                    {l.taxAmount > 0 ? ` + ${formatMoney(l.taxAmount)} tax` : ''} against {l.creditsDocumentNumber ?? l.creditsInvoiceId} ({l.at.slice(0, 10)}
                    {l.note ? `; ${l.note}` : ''})
                  </span>
                  <PillButton size="xs" disabled={busy || voidBusy !== null} onClick={() => { setVoidTarget(l); setVoidReason(''); setErr(null); }}>
                    Void
                  </PillButton>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {releaseAsk ? (
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={confirmRelease} onChange={(e) => setConfirmRelease(e.target.checked)} />
            <span>Release the protected documents the credit note fully settles the invoice for.</span>
          </label>
        ) : null}
        {err ? <div className="rounded-md border border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] px-3 py-2 text-sm text-[var(--tint-red-text)]">{err}</div> : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={voidTarget !== null}
        title="Void this lapsed credit?"
        confirmLabel="Void lapse"
        confirmVariant="danger"
        busy={voidBusy !== null}
        confirmDisabled={!voidReason.trim()}
        onConfirm={() => void doVoid()}
        onCancel={() => { setVoidTarget(null); setVoidReason(''); }}
      >
        {voidTarget ? (
          <>
            <p>
              {voidTarget.unit}, {voidTarget.coversFrom} to {voidTarget.coversTo}: {formatMoney(voidTarget.amount)}
              {voidTarget.taxAmount > 0 ? ` plus ${formatMoney(voidTarget.taxAmount)} of tax` : ''} goes back to being owed; the next composed invoice re-derives it.
            </p>
            <input
              className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-3 py-2 text-sm"
              placeholder="Reason (required)"
              value={voidReason}
              maxLength={500}
              onChange={(e) => setVoidReason(e.target.value)}
            />
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
